import React, { useEffect, useRef, useState, useMemo } from "react";
import { Platform, View } from "react-native";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Heading,
  FlexGrid,
  theme,
  useResponsive,
  Panel,
  Text,
} from "@clearabee/ui-library-native";
import {
  useBasket,
  useCatalogue,
  isCalloutCharge,
  createOrUpdateBasket,
  isQuantitative,
  instance,
  ContextBasketCreate,
  stringToDate,
} from "@clearabee/ui-sdk";
import { Layout } from "components/Layout/Layout";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { SEO } from "components/Seo/Seo";
import { requirePostcodePrice } from "lib/utils";
import {
  API_SKIP_WASTE_TYPES,
  SKIP_WASTE_TYPES,
  WasteType,
} from "lib/constants/skipWasteTypes";
import { useAnalytics } from "lib/hooks/useAnalytics";
import { useRouter } from "lib/hooks/useRouter";
import {
  getInitialValues,
  getValidationSchema,
} from "./ProductDetails.validation";
import { styles } from "./ProductDetails.styles";
import { useQuery } from "react-query";
import { ProductDetailsPanel } from "containers/ProductDetailsPanel/ProductDetailsPanel";
import {
  DateTimePanel,
  RangeDeliveryOption,
} from "components/DateTimePanel/DateTimePanel";
import { DetailsForm } from "components/DetailsForm/DetailsForm";
import { PopulateFormOnLogin } from "./PopulateFormOnLogin";
import { LoginBanner } from "components/LoginBanner/LoginBanner";
import { useSuccessModal } from "lib/hooks/useSuccessModal";
import { useForm, Form } from "lib/hooks/useForm";
import { WasteDescription } from "../WasteDescription/WasteDescription";
import { Grid } from "./Grid";
import { AddToBasket } from "./AddToBasket";
import { useAuth } from "lib/hooks/useAuth";
import { Spinner } from "components/Core/Spinner/Spinner";
import { z } from "zod";
import { useBasketImages } from "lib/hooks/useBasketImages";
import { Basket } from "lib/types";

interface ProductDetailsProps {
  categorySku: string;
  productSku: string;
  wasteType?: string;
  postcode?: string;
  exchangeOrderRef?: string;
  isEditScreen?: boolean;
  initialValues?: z.input<ReturnType<typeof getValidationSchema>> & {
    uploads?: Basket["uploads"];
  };
}

export const ProductDetails = ({
  categorySku,
  productSku,
  wasteType,
  postcode = "",
  exchangeOrderRef,
  isEditScreen = false,
  initialValues: propInitialValues,
}: ProductDetailsProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { ecommerce } = useAnalytics();
  const { user } = useAuth();
  const { replace, navigate } = useRouter();
  const { getCatalogue, defaultCatalogue } = useCatalogue();
  const { screens } = useResponsive();
  const { create, update, basket, isSuccess, error } = useBasket();
  const [SuccessModal, showSuccessModal] = useSuccessModal();
  const catalogue = getCatalogue(postcode) || defaultCatalogue;
  const [isLoading, setLoading] = useState(false);
  const product = catalogue.items.find(({ sku }) => sku === productSku);
  const postcodePrices = requirePostcodePrice(product ? [product] : []);
  const hasSurcharges = categorySku === "MANVAN";
  const surcharges = hasSurcharges
    ? catalogue.items.filter(({ parentSku }) => parentSku === "SURCHARGES")
    : [];
  const { basketImages, imagesAreLoading, setImages, uploadImages } =
    useBasketImages({
      uploads: propInitialValues?.uploads,
    });

  const { data: calloutCharge, isError: isQueryError } = useQuery(
    `getCalloutCharge-${product?.sku}`,
    async () =>
      (await instance.catalogues.getCalloutCharge("hwa", product?.sku ?? ""))
        .data,
    {
      enabled: product && !product.noCalloutCharge,
      retry: false,
    },
  );

  if (!product || isCalloutCharge(product.sku)) {
    return <ErrorPage />;
  }

  const hasProhibitedItems = ["MANVAN", "BINCOL", "SKIPS"].includes(
    categorySku,
  );
  const hasNoCalloutCharge = !!(
    product.noCalloutCharge ||
    isQueryError ||
    (calloutCharge && !calloutCharge.price)
  );

  const quantity =
    Platform.OS === "web" && typeof window !== "undefined"
      ? new URL(window.location.href).searchParams.get("quantity")
      : null;
  const parsedQuantity = quantity ? parseInt(quantity) : undefined;
  const clampedQuantity =
    !parsedQuantity || isNaN(parsedQuantity) || !isQuantitative(product)
      ? undefined
      : Math.min(Math.max(parsedQuantity, 1), 14);
  const quantityRef = useRef(clampedQuantity ?? 1);
  const deliveryOptions =
    product.deliveryOptions ?? product.inherited?.deliveryOptions ?? undefined;

  const rangeDeliveryOptions = deliveryOptions?.find(
    ({ range }) => range?.length,
  ) as RangeDeliveryOption | undefined;

  const { data: blackouts, isLoading: isBlackoutsLoading } = useQuery(
    ["blackouts", categorySku, productSku],
    async () =>
      stringToDate(
        (await instance.catalogues.getCatalogueItemBlackouts("hwa", productSku))
          .data,
      ),
  );

  const showSkipHireCollectionDate = categorySku === "SKIPS";

  const form = useForm({
    onSubmit: async ({
      quantity: qty,
      extras,
      privateLand, // eslint-disable-line @typescript-eslint/no-unused-vars
      prohibitedItems, // eslint-disable-line @typescript-eslint/no-unused-vars
      delivery,
      deliveryCharge,
      collectionDate,
      ...values
    }) => {
      setLoading(true);

      const timeslot = delivery.timeslot
        ? delivery.timeslot.split("-")
        : undefined;
      const body: ContextBasketCreate = {
        ...values,
        date: dayjs.utc(values.date).toISOString(),
        items: [
          {
            sku: product.sku,
            qty,
            price: product.price ?? 0,
          },
          ...(hasSurcharges
            ? surcharges.flatMap(({ sku, price }) => {
                const qty = extras[sku];

                return qty > 0
                  ? {
                      sku,
                      qty,
                      price: price ?? 0,
                    }
                  : [];
              })
            : []),
        ],
        timeslot: timeslot ? { from: timeslot[0], to: timeslot[1] } : null,
        deliveryOption: !!deliveryOptions?.length
          ? delivery.deliveryOption
          : null,
        deliveryCharge: !!deliveryOptions?.length ? deliveryCharge : null,
        meta:
          categorySku === "SKIPS" && wasteType
            ? {
                wasteType: API_SKIP_WASTE_TYPES[wasteType as WasteType],
                ...(!!collectionDate && {
                  collectionDate: dayjs.utc(collectionDate).toISOString(),
                }),
                ...(!!exchangeOrderRef ? { exchangeOrderRef } : {}),
              }
            : {},
      };

      try {
        // if bulkAdd navigate from edit screen, keep the uploads
        // if bulkAdd nagivate from book screen, delete all uploads
        if (!isEditScreen) {
          // delete uploads if switching booking services
          if (basket && basket?.uploads?.length) {
            basket?.uploads.map(async (image) => {
              const name = image.url.split("/").at(-1);
              await instance.catalogues.deleteBasketUpload(
                basket.basketToken,
                name ?? "",
                { params: { type: "photo" } },
              );
            });
          }
        }

        await createOrUpdateBasket(body, { create, update, basket });

        setLoading(false);
        showSuccessModal(true);
      } catch {
        setLoading(false);
      }
    },
    validationSchema: getValidationSchema(
      !!deliveryOptions,
      hasProhibitedItems,
      rangeDeliveryOptions,
      hasSurcharges ? surcharges.map(({ sku }) => sku) : undefined,
      categorySku,
      showSkipHireCollectionDate,
    ),
    defaultValues:
      propInitialValues ||
      getInitialValues(
        postcode,
        deliveryOptions?.[0].title,
        hasProhibitedItems,
        hasSurcharges ? surcharges.map(({ sku }) => sku) : undefined,
        user || null,
        quantityRef.current,
      ),
  });
  const { watch, formState, setValue, clearErrors } = form;
  const { isValid, submitCount, errors } = formState;
  const [quantityValue, deliveryOptionValue, extrasValue] = watch([
    "quantity",
    "delivery.deliveryOption",
    "extras",
  ]);

  const AddToBasketMemo = useMemo(() => {
    return (
      <AddToBasket
        categorySku={categorySku}
        hasSurcharges={hasSurcharges}
        hasProhibitedItems={hasProhibitedItems}
        isValid={isValid}
        submitCount={submitCount}
        error={error || uploadImages.error}
        formErrors={errors}
        onSubmit={form.submitForm}
        isLoading={isLoading}
        disabled={
          isLoading ||
          (!product.noCalloutCharge && !calloutCharge && !isQueryError)
        }
        price={
          (product?.price || 0) * quantityValue +
          (deliveryOptions?.find(({ title }) => title === deliveryOptionValue)
            ?.charge ?? 0) +
          (hasSurcharges
            ? surcharges.reduce(
                (total, { sku, price }) =>
                  total + extrasValue[sku] * (price ?? 0),
                0,
              )
            : 0) +
          (calloutCharge?.price ?? 0)
        }
      />
    );
  }, [
    form,
    product,
    error,
    hasProhibitedItems,
    isValid,
    submitCount,
    deliveryOptions,
    hasSurcharges,
    surcharges,
    calloutCharge,
    isQueryError,
    errors,
    isLoading,
    uploadImages.error,
  ]);

  useEffect(() => {
    if (
      !product.price ||
      (!postcode && postcodePrices) ||
      (categorySku === "SKIPS" &&
        !SKIP_WASTE_TYPES.some(({ value }) => value === wasteType))
    ) {
      navigate({
        routeName: "ServicePage",
        params: { categorySku },
      });
    }
  }, []);

  useEffect(() => {
    if (Platform.OS === "web" && clampedQuantity) {
      replace({
        routeName: "ProductPage",
        params: {
          categorySku,
          productSku,
        },
      });
    }
  }, []);

  useEffect(() => {
    ecommerce.viewItem(product);
  }, []);

  useEffect(() => {
    if (isSuccess) {
      ecommerce.addToCart([{ ...product, qty: 1 }]);
    }
  }, [isSuccess]);

  //uploading images when basket is created successfully
  useEffect(() => {
    if (!isLoading && isSuccess && !!basket) {
      uploadImages.mutateAsync();
    }
  }, [isLoading, isSuccess]);

  return (
    <>
      <SEO
        title={`${product.title} - ${translate(
          "hwa.screens.productDetails.title",
        )}`}
      />
      <Layout
        title={translate("hwa.screens.productDetails.title")}
        backTo={{
          routeName: "ServicePage",
          params: {
            categorySku,
            ...(categorySku !== "SKIPS" && {
              postcode,
              wasteType,
            }),
          },
        }}
      >
        <Form
          state={form}
          key={Platform.OS !== "android" ? undefined : user?.email || null}
        >
          {({ Field }) => (
            <>
              {Platform.OS !== "android" && (
                <>
                  <PopulateFormOnLogin />
                  {!user && <LoginBanner />}
                </>
              )}
              <FlexGrid>
                <FlexGrid.Cell base="100%" large="50%">
                  {!imagesAreLoading && (
                    <ProductDetailsPanel
                      product={product}
                      hasNoCalloutCharge={hasNoCalloutCharge}
                      calloutCharge={calloutCharge}
                      quantity={quantityValue}
                      onImageChange={setImages}
                      categorySku={categorySku}
                      wasteType={wasteType}
                      exchangeOrderRef={exchangeOrderRef}
                      initialImages={basketImages}
                    />
                  )}
                  {imagesAreLoading && (
                    <Panel
                      styles={{
                        height: Platform.OS !== "web" ? 200 : 400,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      shadow={Platform.OS === "web"}
                    >
                      <Spinner size="large" color="accent" />
                    </Panel>
                  )}
                </FlexGrid.Cell>
                <FlexGrid.Cell
                  base="100%"
                  large="50%"
                  style={screens.large && { maxHeight: 432 }}
                >
                  {!isBlackoutsLoading && (
                    <DateTimePanel
                      deliveryOptions={deliveryOptions}
                      blackouts={blackouts ?? []}
                      onListChange={(value) => {
                        setValue(
                          "deliveryCharge",
                          deliveryOptions?.find(({ title }) => title === value)
                            ?.charge ?? 0,
                        );
                        setValue("delivery.timeslot", "");
                        clearErrors("delivery.timeslot");
                      }}
                      selectedOption={deliveryOptionValue}
                      rangeDeliveryOptions={rangeDeliveryOptions}
                      categorySku={categorySku}
                      isExchange={!!exchangeOrderRef}
                    />
                  )}
                  {isBlackoutsLoading && (
                    <Panel
                      styles={{
                        height: Platform.OS !== "web" ? 200 : 400,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      shadow={Platform.OS === "web"}
                    >
                      <Spinner size="large" color="accent" />
                    </Panel>
                  )}
                </FlexGrid.Cell>
              </FlexGrid>

              <Grid>
                <Grid.GridCell gridArea="detailsform">
                  <DetailsForm
                    disablePostcode={
                      !!postcode &&
                      (product.pricingModel === "postcode" ||
                        product.pricingModel === "suppliers")
                    }
                    categorySku={categorySku}
                  />
                </Grid.GridCell>
                {Platform.OS === "web" && (
                  <Grid.GridCell gridArea="submit">
                    {AddToBasketMemo}
                  </Grid.GridCell>
                )}
                <WasteDescription
                  categorySku={categorySku}
                  surcharges={surcharges}
                >
                  {({
                    DescriptionField,
                    SurchargesProhibitedItems,
                    Tooltip,
                    Overlay,
                  }) => (
                    <>
                      <Overlay />
                      <Grid.GridCell
                        gridArea="description"
                        nativeStyle={{ marginBottom: theme.spacing.medium }}
                      >
                        <Field
                          name="description"
                          label={translate("common.wasteDescription")}
                          styles={styles.wasteDescription}
                          labelStyles={
                            Platform.OS === "web" ? styles.label : undefined
                          }
                          errorStyles={screens.large && styles.error}
                          invertErrorColors
                          errorBackground
                        >
                          {({ field }) => (
                            <DescriptionField
                              {...field}
                              multiline
                              placeholder={translate(
                                "common.form.placeholder.wasteDescription",
                              )}
                              styles={{
                                height: theme.spacing.xlarge4,
                                textAlignVertical: "top",
                              }}
                            />
                          )}
                        </Field>
                        <Tooltip />
                      </Grid.GridCell>
                      <Grid.GridCell gridArea="surcharges">
                        <View>
                          <View style={styles.heading}>
                            <Heading
                              level={5}
                              color={Platform.OS === "web" ? "accent" : "brand"}
                            >
                              {hasSurcharges
                                ? translate(
                                    "hwa.screens.productDetails.surcharges",
                                  )
                                : translate(
                                    "hwa.screens.productDetails.prohibitedItems.title",
                                  )}
                            </Heading>
                            {hasSurcharges && (
                              <Text color="light" fontSize="xsmall">
                                {translate(
                                  "hwa.screens.productDetails.additionalCosts",
                                )}
                              </Text>
                            )}
                          </View>

                          <FlexGrid cellSpacing={null}>
                            <SurchargesProhibitedItems />
                          </FlexGrid>
                        </View>
                      </Grid.GridCell>
                    </>
                  )}
                </WasteDescription>
                {Platform.OS !== "web" && (
                  <Grid.GridCell gridArea="submit">
                    {AddToBasketMemo}
                  </Grid.GridCell>
                )}
              </Grid>
            </>
          )}
        </Form>
      </Layout>

      <SuccessModal product={product} />
    </>
  );
};
